import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CaseIdSchema, ImportJobSchema, type ImportJob } from '@casetrace/shared';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

function toImportJobs(
  caseId: string,
  docs: Array<{ id: string; data: () => unknown }>,
): ImportJob[] {
  return docs.map((doc) => {
    const rawData = doc.data();
    return ImportJobSchema.parse({ ...(rawData as Record<string, unknown>), id: doc.id, caseId });
  });
}

async function fetchImportJobs(caseId: string): Promise<ImportJob[]> {
  const parsedCaseId = CaseIdSchema.parse(caseId);
  const ref = collection(db, 'cases', parsedCaseId, 'importJobs');
  const snap = await getDocs(ref);
  return toImportJobs(parsedCaseId, snap.docs);
}

export function useImportJobs(caseId: string) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['import-jobs', caseId] as const,
    queryFn: () => fetchImportJobs(caseId),
    enabled: Boolean(caseId),
  });

  useEffect(() => {
    if (!caseId) return;
    const parsedCaseId = CaseIdSchema.parse(caseId);
    const ref = collection(db, 'cases', parsedCaseId, 'importJobs');
    return onSnapshot(ref, (snap) => {
      queryClient.setQueryData(['import-jobs', caseId], toImportJobs(parsedCaseId, snap.docs));
    });
  }, [caseId, queryClient]);

  return query;
}
